import { useAuth } from '@clerk/nextjs';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { StaleTime } from 'queries/types';
import { ACCOUNT_PREFIX } from 'restful/accounts/constants';
import { restCall } from 'services/api/restful/client';
import { RestApiError } from 'services/api/restful/errors';
import { getAccount } from 'services/api/restful/generated/accounts/accounts';
import type { Account } from 'services/api/restful/generated/nofomoRestApi.schemas';

export type AccountQueryData = { account: Account };

export const accountQuery = queryOptions<AccountQueryData, RestApiError>({
  queryKey: [ACCOUNT_PREFIX, 'me'],
  queryFn: async () => {
    const { data } = await restCall(() => getAccount());
    return { account: data };
  },
});

export const useAccount = () => {
  const { reload } = useRouter();

  const { isSignedIn, isLoaded: clerkIsLoaded } = useAuth();

  const { error, data, isPending } = useQuery({
    ...accountQuery,
    staleTime: StaleTime.MINUTE,
    retry: false,
    refetchOnWindowFocus: true,
    enabled: !!isSignedIn,
  });

  const restError = error instanceof RestApiError ? error : undefined;

  // Edge case: after signIn, Clerk might not yet have the token, resulting in a Unauthorized error.
  // Reload the page to make sure the Clerk client is correctly initialized.
  useEffect(() => {
    if (restError?.isUnauthorized) {
      reload();
    }
  }, [restError, reload]);

  // Was an edge case, mostly in safari where we got this error when refreshing the page multiple times.
  // Query should still be loading state because otherwise we get redirected to the onboarding page.
  const failedToLoadError =
    error instanceof TypeError && error.message === 'Load failed';

  return {
    account: data?.account,
    isLoading: !clerkIsLoaded || (isSignedIn && isPending) || failedToLoadError,
    isNotFound: !!restError?.isNotFound,
    isSignedIn: !!isSignedIn,
  };
};
